import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  Signal,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import { TagColorInfo } from '@blaze-runtime/engine-domain';
import { SharedModule } from '@blaze-shared/shared-feature';
import { ScrollerComponent } from '@blaze-shared/shared-ui';

import { ChatListItemComponent } from '../chat-list-item/chat-list-item.component';
import { ChatConversation, ChatMember } from '../chat.models';

const DEFAULT_CHAT_NAME_PLACEHOLDER = 'Chat name';

@Component({
  selector: 'be-chat-form',
  templateUrl: './chat-form.component.html',
  styleUrls: ['./chat-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChatListItemComponent, SharedModule, ScrollerComponent],
})
export class ChatFormComponent {
  readonly conversation = input<ChatConversation | null>();
  readonly members = input<ChatMember[]>([]);
  readonly membersColors = input<Record<string, TagColorInfo>>({});
  readonly membersTableName = input<string>('');

  readonly save = output<{ name: string; members: ChatMember[] }>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly cancel = output<void>();

  readonly scroller = viewChild(ScrollerComponent);
  readonly textInput = viewChild<ElementRef<HTMLInputElement>>('textInput');
  readonly options = viewChildren(ChatListItemComponent, { read: ElementRef });

  private readonly searchQuery: Signal<string>;

  searchFormControl = new FormControl('', { nonNullable: true });
  chatNameFormControl = new FormControl('', { nonNullable: true });

  readonly selectedMembers = signal<ChatMember[]>([]);
  readonly selectedOptionIndex = signal(-1);

  readonly listBoxId = `${crypto.randomUUID()}-chat-listbox`;
  readonly filteredMembers = computed(() => {
    const query = this.searchQuery().toLocaleLowerCase();
    const allMembers = this.members();
    const selected = this.selectedMembers();

    let filtered = allMembers.slice();

    if (query.length) {
      filtered = allMembers.filter(
        (user) =>
          user.name?.toLocaleLowerCase().includes(query) ||
          user.email?.toLocaleLowerCase().includes(query),
      );
    }

    if (selected.length) {
      filtered = filtered.filter(
        (user) => !selected.some((selectedMember) => selectedMember.id === user.id),
      );
    }

    return filtered;
  });

  readonly placeholder = computed(() =>
    this.selectedMembers().length
      ? this.selectedMembers()
          .map((m) => m.name)
          .join(', ')
      : DEFAULT_CHAT_NAME_PLACEHOLDER,
  );

  readonly hasMembers = computed(() => this.members().length > 0);

  constructor() {
    this.searchQuery = toSignal(this.searchFormControl.valueChanges, {
      initialValue: '',
    });

    toObservable(this.conversation)
      .pipe(takeUntilDestroyed())
      .subscribe((conversation) => {
        if (!conversation) {
          return;
        }

        this.chatNameFormControl.setValue(conversation.name || '');

        const membersList = this.members();
        const initialSelected: ChatMember[] = [];

        for (const memberIdentifier of conversation.members) {
          const ix = membersList.findIndex(
            (m) =>
              m.id === memberIdentifier ||
              m.name === memberIdentifier ||
              m.email === memberIdentifier,
          );

          if (ix !== -1 && !initialSelected.some((user) => user.id === membersList[ix].id)) {
            initialSelected.push(membersList[ix]);
          }
        }

        this.selectedMembers.set(initialSelected);
      });

    this.searchFormControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.selectedOptionIndex.set(0);
    });
  }

  focusNextOption(): void {
    const filtered = this.filteredMembers();
    if (this.selectedOptionIndex() === filtered.length - 1) {
      this.selectedOptionIndex.set(0);
    } else {
      this.selectedOptionIndex.update((i) => i + 1);
    }
    this.scrollMemberIntoView(this.selectedOptionIndex());
  }

  focusPreviousOption(): void {
    const filtered = this.filteredMembers();
    if (this.selectedOptionIndex() === 0) {
      this.selectedOptionIndex.set(filtered.length - 1);
    } else {
      this.selectedOptionIndex.update((i) => i - 1);
    }
    this.scrollMemberIntoView(this.selectedOptionIndex());
  }

  onSelect(index?: number): void {
    this.selectOption(index);

    if (index !== undefined) {
      this.focusTextInput();
    }
  }

  selectOption(index?: number): void {
    const selectedMember = this.filteredMembers()[index ?? this.selectedOptionIndex()];
    if (!selectedMember || this.selectedMembers().some((user) => user.id === selectedMember.id)) {
      return;
    }

    this.selectedMembers.update((members) => [...members, selectedMember]);
    this.searchFormControl.setValue('');
    this.selectedOptionIndex.set(0);
  }

  removeSelection(user: ChatMember): void {
    this.selectedMembers.update((members) => members.filter((m) => m !== user));
  }

  saveChat(): void {
    this.save.emit({
      name: this.chatNameFormControl.value,
      members: this.selectedMembers(),
    });
  }

  closeChat(): void {
    this.cancel.emit();
  }

  private selectMemberByIndex(index: number, membersList: ChatMember[]): void {
    const member = membersList[index];
    if (!member || this.selectedMembers().some((user) => user.id === member.id)) {
      return;
    }

    this.selectedMembers.update((members) => [...members, member]);
  }

  private scrollMemberIntoView(index: number): void {
    const htmlElement = this.options()?.[index]?.nativeElement;
    const viewport = this.scroller()?.getViewport();
    if (!htmlElement || !viewport) {
      return;
    }

    viewport.scrollTop = htmlElement.offsetTop;
  }

  private focusTextInput(): void {
    this.textInput()?.nativeElement.focus();
  }
}
